import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../settings';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

function HealthRecords() {
  const { id } = useParams(); // patient_id
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [patientRes, recordsRes, usersRes] = await Promise.all([
          fetch(`${API_URL}/patients/${id}`),
          fetch(`${API_URL}/health_records?patient_id=${id}`),
          fetch(`${API_URL}/users`)
        ]);

        if (!patientRes.ok || !recordsRes.ok || !usersRes.ok) {
          throw new Error('ไม่สามารถโหลดข้อมูลได้');
        }

        const patientData = await patientRes.json();
        const recordsData = await recordsRes.json();
        const usersData = await usersRes.json();

        // map user id -> name
        const map = {};
        usersData.forEach(u => {
          map[u.id] = u.name;
        });

        setUsersMap(map);
        setPatient(patientData);
        setRecords(
          recordsData.sort(
            (a, b) => new Date(b.checkup_date) - new Date(a.checkup_date)
          )
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!patient) {
    return <Alert severity="info">ไม่พบข้อมูลผู้สูงอายุ</Alert>;
  }

  return (
    <>
      {/* ===== Header ===== */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/patients')}
        >
          กลับ
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          ประวัติการตรวจสุขภาพ: {patient.first_name} {patient.last_name}
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            navigate(`/health-records/new?patient_id=${patient.id}`)
          }
        >
          บันทึกการตรวจใหม่
        </Button>
      </Box>

      {/* ===== Records ===== */}
      {records.length > 0 ? (
        <Grid container spacing={3}>
          {records.map(record => (
            <Grid size={{ xs: 12 }} key={record.id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                      วันที่ตรวจ:{' '}
                      {new Date(record.checkup_date).toLocaleDateString('th-TH')}
                    </Typography>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/health-records/edit/${record.id}?patient_id=${patient.id}`)}
                    >
                      แก้ไข
                    </Button>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: record.record_image ? 8 : 12 }}>
                      <Typography>
                        <strong>ความดันโลหิต:</strong>{' '}
                        {record.blood_pressure
                          ? `${record.blood_pressure.systolic}/${record.blood_pressure.diastolic}`
                          : '-'}{' '}
                        mmHg
                      </Typography>

                      <Typography>
                        <strong>ระดับน้ำตาล:</strong>{' '}
                        {record.blood_sugar ?? '-'} mg/dL
                      </Typography>

                      <Typography>
                        <strong>อัตราชีพจร:</strong>{' '}
                        {record.heart_rate ?? '-'} ครั้ง/นาที
                      </Typography>

                      <Typography>
                        <strong>BMI:</strong> {record.bmi ?? '-'}
                      </Typography>

                      <Typography sx={{ mt: 1 }}>
                        <strong>ผู้บันทึก:</strong>{' '}
                        {usersMap[record.recorded_by] || '-'}
                      </Typography>

                      {record.notes && (
                        <Paper
                          variant="outlined"
                          sx={{ mt: 2, p: 2, backgroundColor: '#f5faff' }}
                        >
                          <Typography>
                            <strong>บันทึกเพิ่มเติม:</strong> {record.notes}
                          </Typography>
                        </Paper>
                      )}
                    </Grid>
                    
                    {record.record_image && (
                      <Grid size={{ xs: 12, md: 4 }}>
                         <Box sx={{ mt: 2, maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
                            <img
                                src={record.record_image}
                                alt="Record Image"
                                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 8, objectFit: 'contain' }}
                            />
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Alert severity="info">ยังไม่มีประวัติการตรวจสุขภาพ</Alert>
      )}
    </>
  );
}

export default HealthRecords;
